<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
      <div>
        <h1 class="h2 mb-1 fw-bold">Organizations</h1>
        <p class="text-muted mb-0">Manage all organizations in the system</p>
      </div>
      <div class="btn-group">
        <a routerLink="/users/create-organization" class="btn btn-primary">
          <i class="bi bi-plus-lg me-2"></i>
          Create Organization
        </a>
      </div>
    </div>

    <!-- Success Alert -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Error Alert -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Filters Card -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <!-- Search -->
          <div class="col-md-4">
            <label class="form-label small text-body-secondary">Search</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search by name, city, state..."
                [(ngModel)]="searchQuery"
                (keyup.enter)="onSearch()"
              />
              <button class="btn btn-outline-secondary" type="button" (click)="onSearch()">
                Search
              </button>
            </div>
          </div>

          <!-- Organization Type Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Organization Type</label>
            <select
              class="form-select"
              [(ngModel)]="filterType"
              (change)="onFilterChange()"
            >
              <option *ngFor="let type of organizationTypes" [value]="type.value">
                {{ type.label }}
              </option>
            </select>
          </div>

          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Business Type</label>
            <select class="form-select"
                    [(ngModel)]="filterBusinessType"
                    (change)="onFilterChange()"
            >
              <option *ngFor="let type of businessTypes" [value]="type.value">
                {{ type.label }}
              </option>
            </select>
          </div>

          <!-- Sort -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Sort By</label>
            <select
              class="form-select"
              [(ngModel)]="sortBy"
              (change)="loadOrganizations()"
            >
              <option value="createdAt">Date Created</option>
              <option value="name">Name</option>
              <option value="city">City</option>
              <option value="state">State</option>
            </select>
          </div>

          <!-- Sort Order -->
          <div class="col-md-auto">
            <label class="form-label small text-body-secondary">Order</label>
            <select
              class="form-select"
              [(ngModel)]="sortOrder"
              (change)="loadOrganizations()"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>

          <!-- Show Archived -->
          <div class="col-md-auto d-flex align-items-end">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="showArchived" [(ngModel)]="showArchived" (change)="onShowArchivedChange()">
              <label class="form-check-label" for="showArchived">Show Archived</label>
            </div>
          </div>

          @if (showClearFilters()) {
            <div class="row mt-3">
              <div class="col-12">
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="resetFilters()">
                  <i class="bi bi-x-circle me-1"></i>
                  Clear Filters
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading organizations...</p>
    </div>

    <!-- Organizations Table -->
    <div *ngIf="!loading" class="card overflow-hidden">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table data-table table-hover mb-0">
            <thead>
              <tr>
                <th class="cursor-pointer" (click)="onSortChange('name')">
                  Organization Name
                  <i *ngIf="sortBy === 'name'" class="bi" [ngClass]="sortOrder === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </th>
                <th>Type</th>
                <th>Business Type</th>
                <th class="cursor-pointer d-none d-lg-table-cell" (click)="onSortChange('city')">
                  Location
                  <i *ngIf="sortBy === 'city'" class="bi" [ngClass]="sortOrder === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </th>
                <th>Contact</th>
                <th class="cursor-pointer" (click)="onSortChange('createdAt')">
                  Created
                  <i *ngIf="sortBy === 'createdAt'" class="bi" [ngClass]="sortOrder === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngIf="organizations.length === 0">
                <td colspan="6" class="text-center py-5 text-muted">
                  <i class="bi bi-building fs-1 d-block mb-2"></i>
                  <span *ngIf="searchQuery || filterType">No organizations found matching your criteria.</span>
                  <span *ngIf="!searchQuery && !filterType">No organizations yet.</span>
                </td>
              </tr>
              <tr
                *ngFor="let org of organizations"
                class="cursor-pointer"
                (click)="viewOrganization(org)"
              >
                <td>
                  <div class="d-flex align-items-center gap-3">
                    <div class="entity-avatar entity-avatar-square">
                      <i class="bi bi-building"></i>
                    </div>
                    <div>
                      <div class="fw-medium">
                        {{ org.name }}
                        <span *ngIf="org.archivedAt" class="badge bg-warning text-dark ms-2">Archived</span>
                      </div>
                      <small class="text-muted" *ngIf="org.parentOrganizationId">
                        <i class="bi bi-diagram-3 me-1"></i>
                        Subsidiary
                      </small>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="badge" [ngClass]="getOrganizationTypeClass(org.organizationType)">
                    {{ getOrganizationTypeLabel(org.organizationType) }}
                  </span>
                </td>
                <td>
                  <span class="badge" [ngClass]="getBusinessTypeClass(org.businessType)">
                    {{ getBusinessTypeLabel(org.businessType) }}
                  </span>
                </td>
                <td class="d-none d-lg-table-cell">
                  <div>{{ org.city }}, {{ org.state }}</div>
                  <small class="text-muted">{{ org.country }}</small>
                </td>
                <td>
                  <div *ngIf="org.email">
                    <i class="bi bi-envelope me-1 text-muted"></i>
                    {{ org.email }}
                  </div>
                  <div *ngIf="org.phone">
                    <i class="bi bi-telephone me-1 text-muted"></i>
                    {{ org.phone | phoneFormat }}
                  </div>
                  <span *ngIf="!org.email && !org.phone" class="text-muted">-</span>
                </td>
                <td>
                  {{ formatDate(org.createdAt) }}
                </td>
                <td class="text-end" (click)="$event.stopPropagation()">
                  <a
                    [routerLink]="['/organizations', org.id]"
                    class="btn btn-sm btn-outline-primary me-1"
                    title="View Details"
                  >
                    <i class="bi bi-eye"></i>
                  </a>
                  <a
                    [routerLink]="['/organizations', org.id, 'edit']"
                    class="btn btn-sm btn-outline-secondary"
                    title="Edit"
                  >
                    <i class="bi bi-pencil"></i>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination Footer -->
      <div class="card-footer d-flex justify-content-between align-items-center" *ngIf="totalOrganizations > 0">
        <div class="text-muted">
          Showing {{ showingStart }} to {{ showingEnd }} of {{ totalOrganizations }} organizations
        </div>
        <nav *ngIf="totalPages > 1">
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" [class.disabled]="currentPage === 1">
              <button class="page-link" (click)="onPageChange(currentPage - 1)">
                <i class="bi bi-chevron-left"></i>
              </button>
            </li>
            <li
              *ngFor="let page of paginationRange"
              class="page-item"
              [class.active]="page === currentPage"
            >
              <button class="page-link" (click)="onPageChange(page)">{{ page }}</button>
            </li>
            <li class="page-item" [class.disabled]="currentPage === totalPages">
              <button class="page-link" (click)="onPageChange(currentPage + 1)">
                <i class="bi bi-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</div>
